import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import { IconLoader, IconMicrophone, IconMicrophoneOff, IconPhoto, IconUpload } from '@tabler/icons-react';
import HighlightExt from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Modal, message } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyImagePickerItem } from '../../components/MyImage/MyImagePickerItem';
import { axiosWithCSRF } from '../../fetchHooks/axiosWithCSRF';
import { envApiUrl } from '../../fetchHooks/envApiUrl';
import { useImages } from '../../fetchHooks/useImages';
import './MydayDetails.scss';
import type { MydayType } from '../../fetchHooks/useMyday';

// Constants
const ALLOWED_AUDIO_TYPES = ['audio/mp3', 'audio/mpeg', 'audio/mp4', 'audio/m4a', 'audio/wav', 'audio/x-m4a'];
const ALLOWED_AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav'];
const MAX_AUDIO_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const transcribeAudioRequest = async (audioFile: Blob | File, filename: string) => {
  const formData = new FormData();
  formData.append('audio', audioFile, filename);
  formData.append('language', 'no'); // Force Norwegian for testing

  const response = await axiosWithCSRF({
    method: 'POST',
    url: `${envApiUrl}/mydays/transcribe-audio`,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
    },
  });

  return response.data;
};

// CSS for spinning animation
const spinStyle = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Custom Image extension with resizing capabilities
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: '300px',
        renderHTML: (attributes) => {
          if (attributes.width) {
            return { style: `width: ${attributes.width};` };
          }
          return {};
        },
      },
      height: {
        default: null,
        renderHTML: (attributes) => {
          if (attributes.height) {
            return { style: `height: ${attributes.height};` };
          }
          return {};
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const styles = [];
    if (HTMLAttributes.style) {
      styles.push(HTMLAttributes.style);
    }
    if (HTMLAttributes.width) {
      styles.push(`width: ${HTMLAttributes.width};`);
    }
    if (HTMLAttributes.height) {
      styles.push(`height: ${HTMLAttributes.height};`);
    }

    return [
      'img',
      {
        ...HTMLAttributes,
        style: styles.join(' '),
      },
    ];
  },
});

interface MyRichTextEditorProps {
  text?: string;
  onChange?: (html: string) => void;
  myday?: MydayType;
  editorClassName?: string;
}
const MyRichTextEditor = ({ text = '', onChange, myday, editorClassName = '' }: MyRichTextEditorProps) => {
  const [content, setContent] = useState(text);
  const [isDragOver, setIsDragOver] = useState(false);
  const { t } = useTranslation();

  const editor = useEditor({
    extensions: [
      CustomImage,
      StarterKit,
      Superscript,
      Subscript,
      HighlightExt,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
      onChange?.(html);
    },
  });

  // Audio file processing function
  const processAudioFile = async (audioFile: File) => {
    if (audioFile.size > MAX_AUDIO_FILE_SIZE) {
      message.error(t('editor.fileSizeTooLarge'));
      return;
    }

    message.loading(`📤 ${t('editor.uploadingAndProcessing')}`, 0);
    try {
      const result = await transcribeAudioRequest(audioFile, audioFile.name);

      if (result?.text?.trim()) {
        editor.chain().focus().insertContent(`<p>${result.text.trim()}</p>`).run();
        message.destroy();
        message.success('✅ Audio file transcribed successfully!');
      } else {
        message.destroy();
        message.warning('⚠️ No speech detected in the audio file.');
      }
    } catch (error) {
      console.error('Error processing audio file:', error);
      message.destroy();
      message.error('❌ Failed to transcribe audio file. Please try again.');
    }
  };

  // Drag and drop handlers for audio files
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const fileExtension = file.name.toLowerCase().split('.').pop() || '';
      const isValidMimeType = ALLOWED_AUDIO_TYPES.includes(file.type);
      const isValidExtension = ALLOWED_AUDIO_EXTENSIONS.includes(fileExtension);

      if (isValidMimeType || isValidExtension) {
        await processAudioFile(file);
      } else {
        message.error(t('editor.pleaseDropValidAudioFile'));
      }
    }
  };

  return (
    <div
      className="tiptap-editor"
      style={{
        position: 'relative',
        borderColor: isDragOver ? '#1890ff' : undefined,
        backgroundColor: isDragOver ? '#f0f8ff' : undefined,
        borderStyle: isDragOver ? 'dashed' : undefined,
        borderWidth: isDragOver ? '2px' : undefined,
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <style>{spinStyle}</style>

      {isDragOver && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '2px dashed #1890ff',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#1890ff',
            }}
          >
            🎵 {t('editor.dropAudioFileHere')}
          </div>
        </div>
      )}

      <RichTextEditor editor={editor} className={editorClassName}>
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
            <RichTextEditor.Highlight />
            <RichTextEditor.Code />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Blockquote />
            <RichTextEditor.Hr />
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
            <RichTextEditor.Subscript />
            <RichTextEditor.Superscript />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.AlignLeft />
            <RichTextEditor.AlignCenter />
            <RichTextEditor.AlignJustify />
            <RichTextEditor.AlignRight />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <InsertImageControl myday={myday} />
            <ImageResizeControl />
            <AudioRecordingControl />
            <AudioFileUploadControl />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content />
      </RichTextEditor>
    </div>
  );
};

function InsertImageControl({ myday }: { myday?: MydayType }) {
  const { editor } = useRichTextEditorContext();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { data: images, isLoading } = useImages(myday?.id || 0);

  if (!editor) return null;

  const addImage = (imageSrc: string) => {
    if (imageSrc) {
      editor
        .chain()
        .focus()
        .setImage({
          src: String(imageSrc),
          width: 300,
          height: 0,
        })
        .run();
      setShowModal(false);
    }
  };

  return (
    <>
      <RichTextEditor.Control
        onClick={() => setShowModal(true)}
        aria-label={t('editor.insertImage')}
        title={t('editor.insertImage')}
      >
        <IconPhoto stroke={1.5} size={16} />
      </RichTextEditor.Control>

      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
        width={800}
        title={t('editor.pickImageToInsert')}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</div>
        ) : images?.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {images.map(({ id, name }: { id: number; name: string }) => (
              <MyImagePickerItem
                key={id}
                alt={name}
                src={`${envApiUrl}/files/image/${id}`}
                onClick={() => addImage(`${envApiUrl}/files/image/${id}`)}
                onClose={() => setShowModal(false)}
                style={{ maxWidth: '300px', maxHeight: '300px' }}
              />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>{t('editor.noImagesAvailable')}</p>
          </div>
        )}
      </Modal>
    </>
  );
}

function ImageResizeControl() {
  const { editor } = useRichTextEditorContext();
  const { t } = useTranslation();

  if (!editor) return null;

  const resizeImage = (width: string | number) => {
    const { state } = editor;
    const { selection } = state;
    const { $from, $to } = selection;

    // Find the image in the current selection
    let imagePos: number | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let imageNode: any = null;

    state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if (node.type.name === 'image') {
        imagePos = pos;
        imageNode = node;
        return false; // Stop traversal
      }
    });

    const widthStr = typeof width === 'number' ? `${width}px` : width;

    if (imagePos !== null && imageNode && imageNode.attrs) {
      // Create a new image node with updated attributes
      const newAttrs = { ...imageNode.attrs, width: widthStr };

      // Replace the image node with the updated one
      editor.chain().focus().setNodeSelection(imagePos).updateAttributes('image', newAttrs).run();

      // Apply styles directly to DOM immediately
      const editorElement = editor.view.dom;
      const images = editorElement.querySelectorAll('img');

      images.forEach((img) => {
        img.style.width = widthStr;
        img.style.height = 'auto';
        img.style.maxWidth = '100%';
      });
    } else {
      // If no image in selection, find the first image and resize it
      state.doc.descendants((node, pos) => {
        if (node.type.name === 'image') {
          imagePos = pos;
          imageNode = node;
          return false; // Stop traversal
        }
      });

      if (imagePos !== null && imageNode && imageNode.attrs) {
        const newAttrs = { ...imageNode.attrs, width: widthStr };

        editor.chain().focus().setNodeSelection(imagePos).updateAttributes('image', newAttrs).run();

        // Apply styles directly to DOM immediately
        const editorElement = editor.view.dom;
        const images = editorElement.querySelectorAll('img');

        images.forEach((img) => {
          img.style.width = widthStr;
          img.style.height = 'auto';
          img.style.maxWidth = '100%';
        });
      }
    }
  };

  const isImageSelected = () => {
    const { state } = editor;
    const { selection } = state;

    // Simple approach: check if the current selection contains an image
    const { $from, $to } = selection;

    // Check if we're directly on an image node
    if ($from.node() && $from.node().type.name === 'image') {
      return true;
    }

    // Check if there's an image in the selected range
    let hasImage = false;
    state.doc.nodesBetween($from.pos, $to.pos, (node) => {
      if (node.type.name === 'image') {
        hasImage = true;
        return false; // Stop traversal
      }
    });

    // If no image in selection, check if there are any images in the document
    if (!hasImage) {
      let docHasImages = false;
      state.doc.descendants((node) => {
        if (node.type.name === 'image') {
          docHasImages = true;
          return false; // Stop traversal
        }
      });
      return docHasImages; // Enable button if there are images in the document
    }

    return hasImage;
  };

  return (
    <RichTextEditor.Control
      onClick={() => {
        const isSelected = isImageSelected();
        if (isSelected) {
          // Get current width from DOM instead of TipTap model
          const editorElement = editor.view.dom;
          const images = editorElement.querySelectorAll('img');
          let currentWidth = '300px'; // default

          if (images.length > 0) {
            const img = images[0];
            currentWidth = img.style.width || '300px';
          }

          const newWidth = currentWidth === '300px' ? '500px' : currentWidth === '500px' ? '200px' : '300px';
          resizeImage(newWidth as string);
        }
      }}
      aria-label={t('editor.resizeImage')}
      title={t('editor.resizeImage')}
      disabled={!isImageSelected()}
    >
      📏
    </RichTextEditor.Control>
  );
}

function AudioRecordingControl() {
  const { editor } = useRichTextEditorContext();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  if (!editor) return null;

  const processAudio = async (audioBlob: Blob) => {
    setIsProcessing(true);
    try {
      const result = await transcribeAudioRequest(audioBlob, 'recording.wav');

      if (result?.text?.trim()) {
        editor.chain().focus().insertContent(`<p>${result.text.trim()}</p>`).run();
        message.destroy();
        message.success(`✅ ${t('editor.audioTranscribedSuccessfully')}`);
      } else {
        message.destroy();
        message.warning(`⚠️ ${t('editor.noSpeechDetected')}`);
      }
    } catch (error) {
      console.error('Error processing audio:', error);
      message.destroy();
      message.error(`❌ ${t('editor.failedToTranscribe')}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await processAudio(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      message.loading(`🎤 ${t('editor.recording')}`, 0);
    } catch (error) {
      console.error('Error starting recording:', error);
      message.error(t('editor.failedToStartRecording'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      message.destroy();
      message.loading(`🔄 ${t('editor.processing')}`, 0);
    }
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <RichTextEditor.Control
      onClick={handleClick}
      aria-label={t('editor.recordAudio')}
      title={
        isRecording ? t('editor.stopRecording') : isProcessing ? t('editor.processingAudio') : t('editor.recordAudio')
      }
      disabled={isProcessing}
    >
      {isProcessing ? (
        <IconLoader stroke={1.5} size={16} style={{ animation: 'spin 1s linear infinite' }} />
      ) : isRecording ? (
        <IconMicrophoneOff stroke={1.5} size={16} style={{ color: 'red' }} />
      ) : (
        <IconMicrophone stroke={1.5} size={16} />
      )}
    </RichTextEditor.Control>
  );
}

function AudioFileUploadControl() {
  const { editor } = useRichTextEditorContext();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!editor) return null;

  const processAudioFile = async (audioFile: File) => {
    const fileExtension = audioFile.name.toLowerCase().split('.').pop() || '';
    const isValidMimeType = ALLOWED_AUDIO_TYPES.includes(audioFile.type);
    const isValidExtension = ALLOWED_AUDIO_EXTENSIONS.includes(fileExtension);

    if (!isValidMimeType && !isValidExtension) {
      message.error(t('editor.pleaseSelectValidAudioFile', { type: audioFile.type || 'unknown type' }));
      return;
    }

    if (audioFile.size > MAX_AUDIO_FILE_SIZE) {
      message.error(t('editor.fileSizeTooLarge'));
      return;
    }

    setIsProcessing(true);
    message.loading(`📤 ${t('editor.uploadingAndProcessing')}`, 0);

    try {
      const result = await transcribeAudioRequest(audioFile, audioFile.name);

      if (result?.text?.trim()) {
        editor.chain().focus().insertContent(`<p>${result.text.trim()}</p>`).run();
        message.destroy();
        message.success(`✅ ${t('editor.audioFileTranscribedSuccessfully')}`);
      } else {
        message.destroy();
        message.warning(`⚠️ ${t('editor.noSpeechDetectedInFile')}`);
      }
    } catch (error) {
      console.error('Error processing audio file:', error);
      message.destroy();
      message.error(`❌ ${t('editor.failedToTranscribeFile')}`);
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processAudioFile(file);
    }
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp3,.m4a,.wav,audio/mp3,audio/mpeg,audio/mp4,audio/m4a,audio/wav,audio/x-m4a"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <RichTextEditor.Control
        onClick={handleClick}
        aria-label={t('editor.uploadAudioFile')}
        title={isProcessing ? t('editor.processingAudio') : t('editor.uploadAudioFile')}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <IconLoader stroke={1.5} size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <IconUpload stroke={1.5} size={16} />
        )}
      </RichTextEditor.Control>
    </>
  );
}

export default MyRichTextEditor;
